import { ApiInstance } from '../instance';
import { firebase, auth } from '../../utils/firebase';

export interface BarLicenseEntry {
  state: string;
  barNumber: string;
  isPrimary: boolean;
}

export type AuthPayload = {
  email: string;
  password: string;
  name?: string;
  orgName?: string;
  barLicenses?: BarLicenseEntry[];
  officeStreet?: string;
  officeCity?: string;
  officeState?: string;
  officeZip?: string;
};

export const AuthController = {
  register: async (payload: AuthPayload) => {
    const {
      email,
      password,
      name,
      orgName,
      barLicenses,
      officeStreet,
      officeCity,
      officeState,
      officeZip,
    } = payload;
    const { user } = await auth.createUserWithEmailAndPassword(email, password);

    if (!user) throw new Error('Registration failed. please try again later');

    return await ApiInstance.post('/advertisers/auth/register', {
      user,
      name,
      orgName,
      role: 'advertiser',
      barLicenses,
      officeStreet,
      officeCity,
      officeState,
      officeZip,
    });
  },

  login: async (payload: AuthPayload) => {
    const { email, password } = payload;
    const { user } = await auth.signInWithEmailAndPassword(email, password);

    if (!user) throw new Error('Login failed. please try again later');

    return await ApiInstance.post('/advertisers/auth/login', {
      token: await user.getIdToken(),
    });
  },

  socialLogin: async (payload: {
    credential: firebase.auth.UserCredential;
    name?: string;
    orgName?: string;
    barLicenses?: BarLicenseEntry[];
    officeStreet?: string;
    officeCity?: string;
    officeState?: string;
    officeZip?: string;
  }) => {
    const {
      credential,
      name,
      orgName,
      barLicenses,
      officeStreet,
      officeCity,
      officeState,
      officeZip,
    } = payload;
    if (!credential || !credential.user)
      throw new Error(`Social Login failed. please try again later`);

    const idToken = await credential.user.getIdToken();

    return await ApiInstance.post('/advertisers/auth/social-login', {
      token: idToken,
      role: 'advertiser',
      email: credential.user.email,
      name: name || credential.user.displayName,
      orgName: orgName || credential.user.displayName,
      barLicenses,
      officeStreet,
      officeCity,
      officeState,
      officeZip,
    });
  },

  resetPassword: async (email: string) => {
    await auth.sendPasswordResetEmail(email);
  },

  logout: async () => {
    // Clear backend session (HttpOnly cookie + DB token), then sign out of Firebase
    try {
      await ApiInstance.post('/advertisers/auth/logout');
    } catch {
      // Continue logout even if backend call fails
    }
    await auth.signOut();
  },

  checkEmailExists: async (email: string): Promise<boolean> => {
    const response = await ApiInstance.get(`/advertisers/auth/check-email`, {
      params: { email },
    });
    const data = response.data;

    return data.exists;
  },
  getAPIKey: async (): Promise<any> => {
    const response = await ApiInstance.post(`/advertisers/auth/get-partner-api-key`);
    const data = response.data;

    return data.data;
  },

  // Trusted device methods for "Remember this device for 30 days" MFA bypass
  trustDevice: async (firebaseToken: string, deviceName?: string): Promise<{ deviceToken: string; expiresAt: string }> => {
    const response = await ApiInstance.post('/advertisers/auth/trust-device', {
      firebaseToken,
      deviceName,
    });
    return response.data;
  },

  verifyTrustedDevice: async (deviceToken: string, email: string): Promise<{ trusted: boolean; customToken?: string }> => {
    try {
      const response = await ApiInstance.post('/advertisers/auth/verify-trusted-device', {
        deviceToken,
        email,
      });
      return response.data;
    } catch (error: any) {
      // Return not trusted for any error (expired, not found, etc.)
      return { trusted: false };
    }
  },

  revokeTrustedDevice: async (deviceToken: string): Promise<void> => {
    await ApiInstance.post('/advertisers/auth/revoke-trusted-device', {
      deviceToken,
    });
  },
};
